/**
 * Ticket Card Component
 * Displays a summary card for a ticket in list views
 */

import { Card, CardContent, CardActionArea, Box, Typography, Chip } from '@mui/material'
import { ChatBubbleOutline, AccessTime } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { TicketSummaryDto } from '@api/types/tickets'
import { TicketStatusBadge } from './TicketStatusBadge'
import { TicketPriorityIndicator } from './TicketPriorityIndicator'
import { TicketTypeIcon } from './TicketTypeIcon'
import { safeFormatDistanceToNow } from '@utils/dateUtils'
import { useAuthStore } from '@store/authStore'

interface TicketCardProps {
  ticket: TicketSummaryDto
  onClick?: (ticketId: string) => void
  showAssignee?: boolean
}

export function TicketCard({ ticket, onClick, showAssignee = false }: TicketCardProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const handleClick = () => {
    if (onClick) {
      onClick(ticket.id)
    } else {
      navigate(`/support/tickets/${ticket.id}`)
    }
  }

  const timeAgo = safeFormatDistanceToNow(ticket.lastUpdatedAt)

  // Normalize IDs for comparison (handle case, whitespace, hyphens in GUIDs)
  const normalizeId = (id: string | null | undefined): string => {
    if (!id) return ''
    return id.toLowerCase().trim().replace(/[-\s]/g, '')
  }

  // Check if ticket is claimed by someone else
  const isClaimedByOther = ticket.claimedByUserId && normalizeId(ticket.claimedByUserId) !== normalizeId(user?.userId)

  return (
    <Card
      sx={{
        mb: 2,
        '&:hover': { boxShadow: 4 },
        ...(isClaimedByOther && {
          borderLeft: '4px solid',
          borderLeftColor: 'warning.main',
          backgroundColor: 'warning.light',
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9))',
        })
      }}
    >
      <CardActionArea onClick={handleClick}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Type Icon */}
            <Box sx={{ pt: 0.5 }}>
              <TicketTypeIcon type={ticket.type} size="medium" />
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Title and Badges */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {ticket.title}
                </Typography>
                {isClaimedByOther && (
                  <Chip label="CLAIMED" size="small" color="warning" sx={{ fontWeight: 'bold' }} />
                )}
                {ticket.isEscalated && (
                  <Chip label="ESCALATED" size="small" color="error" sx={{ fontWeight: 'bold' }} />
                )}
              </Box>

              {/* Metadata Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <TicketStatusBadge status={ticket.status} />
                <TicketPriorityIndicator priority={ticket.priority} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <ChatBubbleOutline fontSize="small" />
                  <Typography variant="caption">{ticket.commentCount}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <AccessTime fontSize="small" />
                  <Typography variant="caption">{timeAgo}</Typography>
                </Box>
              </Box>

              {/* User Information */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Created by: <strong>{ticket.createdByDisplayName || 'Unknown'}</strong>
                </Typography>

                {showAssignee && ticket.claimedByDisplayName && (
                  <Typography variant="caption" color="text.secondary">
                    Claimed by: <strong>{ticket.claimedByDisplayName}</strong>
                  </Typography>
                )}

                {showAssignee && ticket.assignedToDisplayName && (
                  <Typography variant="caption" color="text.secondary">
                    Assigned to: <strong>{ticket.assignedToDisplayName}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
